import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

const PreviewList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState([]);
  const [previewList, setPreviewList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PREVIEW_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isPreviewListLoading, setIsPreviewListLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { creatorList, fetchSearchInfo } = useSearchInfo();

  useEffect(() => {
    fetchData();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage, sortColumn, sortDirection]);

   useEffect(() => {
      fetchData();
  }, [currentPage, sortColumn, sortDirection]);

  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  const columns = ["projectPreviewName", "licensePolicyName", "createrName", "createdAt"];

  const columnConfig = {
    projectPreviewName: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("project.preview-name"),
    },
    licensePolicyName: {
      style: {
        width: "30%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("project.policy-name"),
    },
    createrName: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("system.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("common.create-at"),
      customRender: (value, row) => formatDateTime(row.createdAt),
    },
  };

  const searchFields = [
    { name: "projectPreviewName", label: "Preview Name", placeholder: t("project.preview-name") },
    {
      name: "createrId",
      label: "Creator",
      type: "select",
      keepRawLabel: true,
      options: creatorList,
      displayFormatLabelWithID: true,
      placeholder: t("system.creator-placeholder"),
    },
    {
      name: "createdAtFrom",
      label: "Creation Date (From)",
      placeholder: t("common.create-date-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: "Creation Date (To)",
      placeholder: t("common.create-date-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleDeleteProjectPreview = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.deleteProjectPreviews,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.PREVIEWS, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        setSelectedRows([]);
        showSuccessToast(t("project.delete-project-preview-successfully"));
        queryClient.invalidateQueries(queryKeys.getProjectPreviews);
        // Invalidate all project queries to ensure any project using the deleted preview gets updated
        queryClient.invalidateQueries(queryKeys.getProjectDetail);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        showErrorToast(t("project.delete-project-preview-failed"));
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      await deleteData.mutateAsync(bodyRequest);
      setSelectedRows([]);
      // Close modal
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting project previews:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddProjectPreview = () => {
    navigate("/project-previews/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.projectPreviewSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    bodyRequest["sortKey"] = sortColumn || "createdAt";
    bodyRequest["sortOrder"] = sortDirection || "desc";
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const getListData = async (bodyRequest = {}) => {
    setIsPreviewListLoading(true);
    try {
      const {
        pageSize = itemsPerPage,
        startRow = 0,
        sortKey = sortColumn || "createdAt",
        sortOrder = sortDirection || "desc",
        ...filters
      } = bodyRequest;
      const response = await getIndexDBData(STORES.PREVIEWS, {
        pageSize,
        startRow,
        filters,
        sortKey,
        sortOrder,
      });

      if (response && typeof response !== "string") {
        setPreviewList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching project previews from IndexedDB:", error);
      showErrorToast(t("project.project-preview.get-preview-list-error"));
    } finally {
      setIsPreviewListLoading(false);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleSort = (columnKey, direction) => {
    setSortColumn(columnKey);
    setSortDirection(direction);
  };

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="preview-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="preview-list-header">
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`} data-testid="preview-list-title">{t("project.previews-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="preview-list-controls">
        <TableHeaderControls
          controlsTestId="preview-list-table-header-controls"
          searchInputTestId="preview-list-search-input"
          addButtonTestId="preview-list-add-button"
          deleteButtonTestId="preview-list-delete-button"
          advancedSearchButtonTestId="preview-list-advanced-search-button"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDeleteProjectPreview}
          onAdd={handleAddProjectPreview}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("project.add-preview")}
          titleDeleteButton={t("project.project-preview.delete-preview")}
          isDisplayDeleteButton={hasDeletePermission}
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="preview-list-advanced-search">
          <AdvancedSearch
            data-testid="preview-list-advanced-search-panel"
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Preview Table */}
      <div className="flex-grow min-h-0" data-testid="preview-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={previewList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isPreviewListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="projectPreviewSeq"
          shouldShowGoToPage={true}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          showItemsPerPageSelector={true}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          sortable ={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="preview-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("project.project-preview.delete-preview"))}
        description={t("project.project-preview.delete-preview-warning", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
};

export default PreviewList;
